/**
 * Fondo decorativo del login — moléculas + hexágonos flotando lentamente.
 *
 * Estética farma pero institucional:
 *   - Hexágonos: estructura química (anillo aromático, blíster).
 *   - Moléculas (nodos + bonds): traza, conectividad — metáfora del producto.
 *   - Opacidad ~8% sobre el gradiente azul→naranja del Login para no competir
 *     con el formulario; los elementos flotan en loops ease-in-out de 18-26s.
 *
 * Diseño calm y simétrico (brandbook §"Motion"): sin bounces, sin springs.
 * Respeta `prefers-reduced-motion` desde globals.css (las clases float-* se
 * desactivan automáticamente).
 *
 * Renderizado: SVG inline absolutamente posicionado, `pointer-events: none`
 * para no interferir con el formulario. `aria-hidden` (decorativo puro).
 */

export function MoleculesBackground() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1200 800"
      preserveAspectRatio="xMidYMid slice"
    >
      {/* Hexágono grande arriba-izquierda */}
      <g className="float-1" style={{ transformOrigin: '180px 140px' }}>
        <Hexagon cx={180} cy={140} r={70} stroke="#ffffff" opacity={0.08} />
      </g>

      {/* Molécula central-izquierda (4 nodos conectados) */}
      <g className="float-2" style={{ transformOrigin: '260px 460px' }}>
        <Molecule
          nodes={[
            { x: 200, y: 420 },
            { x: 280, y: 460 },
            { x: 240, y: 540 },
            { x: 320, y: 500 },
          ]}
          edges={[
            [0, 1],
            [1, 2],
            [1, 3],
            [2, 3],
          ]}
          stroke="#ffffff"
          opacity={0.09}
        />
      </g>

      {/* Hexágono medio centro-arriba */}
      <g className="float-3" style={{ transformOrigin: '620px 200px' }}>
        <Hexagon cx={620} cy={200} r={45} stroke="#ffffff" opacity={0.07} />
      </g>

      {/* Anillo aromático (hexágono + doble enlace interno) */}
      <g className="float-1" style={{ transformOrigin: '980px 360px' }}>
        <Hexagon cx={980} cy={360} r={60} stroke="#ffffff" opacity={0.08} />
        {/* doble enlace interno (3 lados alternos) */}
        <Hexagon cx={980} cy={360} r={42} stroke="#ffffff" opacity={0.06} dash />
      </g>

      {/* Molécula triangular abajo derecha */}
      <g className="float-2" style={{ transformOrigin: '900px 640px' }}>
        <Molecule
          nodes={[
            { x: 880, y: 600 },
            { x: 960, y: 640 },
            { x: 900, y: 700 },
          ]}
          edges={[
            [0, 1],
            [1, 2],
            [0, 2],
          ]}
          stroke="#ffffff"
          opacity={0.09}
        />
      </g>

      {/* Hexágono chico abajo izquierda */}
      <g className="float-3" style={{ transformOrigin: '120px 680px' }}>
        <Hexagon cx={120} cy={680} r={32} stroke="#ffffff" opacity={0.07} />
      </g>

      {/* Cadena lineal flotante (3 átomos en línea) — centro derecha */}
      <g className="float-1" style={{ transformOrigin: '1080px 580px' }}>
        <Molecule
          nodes={[
            { x: 1040, y: 540 },
            { x: 1080, y: 580 },
            { x: 1120, y: 620 },
          ]}
          edges={[
            [0, 1],
            [1, 2],
          ]}
          stroke="#ffffff"
          opacity={0.08}
        />
      </g>

      {/* Hexágono mediano centro abajo */}
      <g className="float-2" style={{ transformOrigin: '560px 700px' }}>
        <Hexagon cx={560} cy={700} r={38} stroke="#ffffff" opacity={0.06} />
      </g>
    </svg>
  );
}

interface HexagonProps {
  cx: number;
  cy: number;
  r: number;
  stroke: string;
  opacity: number;
  dash?: boolean;
}

function Hexagon({ cx, cy, r, stroke, opacity, dash }: HexagonProps) {
  // Hexágono regular orientado punta-arriba
  const points: string = Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  }).join(' ');
  return (
    <polygon
      points={points}
      fill="none"
      stroke={stroke}
      strokeOpacity={opacity}
      strokeWidth={1.5}
      strokeDasharray={dash ? '4 4' : undefined}
    />
  );
}

interface MoleculeProps {
  nodes: ReadonlyArray<{ x: number; y: number }>;
  edges: ReadonlyArray<readonly [number, number]>;
  stroke: string;
  opacity: number;
}

function Molecule({ nodes, edges, stroke, opacity }: MoleculeProps) {
  return (
    <>
      {edges.map(([a, b], i) => {
        const na = nodes[a];
        const nb = nodes[b];
        if (!na || !nb) return null;
        return (
          <line
            key={`e${i}`}
            x1={na.x}
            y1={na.y}
            x2={nb.x}
            y2={nb.y}
            stroke={stroke}
            strokeOpacity={opacity}
            strokeWidth={1.5}
          />
        );
      })}
      {nodes.map((n, i) => (
        <circle
          key={`n${i}`}
          cx={n.x}
          cy={n.y}
          r={5}
          fill={stroke}
          fillOpacity={opacity * 1.2}
        />
      ))}
    </>
  );
}
